
















































































import Market from '@core/models/offer/Market';
import {
    defineComponent,
    PropType,
    computed,
} from '@vue/composition-api';
import EventCardLabel from './EventCardLabel.vue';
import EventPin from './EventPin.vue';
import Pick from './Pick.vue';
import { NON_FINAL_MAX_ODDS } from '@core/services/offerPresentation/OfferPresentationService';
import { useVuexStore } from '@superbet-group/web.lib.vue-utilities';
import { SportOfferMarketsStore } from '@store/modules/ui/sportOffer/markets';
import Odd from '@core/models/offer/Odd';
import { ExperimentsGroup } from '@superbet-group/product.analytics';
import { getPe131DescriptiveOddButtonVariant } from '@app/shared/featureFlags/pe131DescriptiveOddButtonFlag';

export default defineComponent({
    name: 'OddOffer',
    emits: [
        'oddClick',
        'pinClick',
    ],
    props: {
        marketCount: {
            type: Number,
            default: 0,
        },
        moreOdds: {
            type: Number,
            default: 0,
        },
        market: {
            type: Object as PropType<Market | null>,
            default: null,
        },
        expandedEvent: {
            type: Number,
            default: null,
        },
        isEventLocked: {
            type: Boolean,
            default: false,
        },
        selectedOdds: {
            type: Set as PropType<Set<string>>,
            required: true,
        },
        oddValueDynamics: {
            type: Object,
            required: false,
            default: undefined,
        },
        isPrimaryMarketSelected: {
            type: Boolean,
            default: true,
        },
        isPinShown: {
            type: Boolean,
            required: true,
        },
        isEventPinned: {
            type: Boolean,
            required: false,
            default: false,
        },
        hideMarkerHeader: {
            type: Boolean,
            required: false,
            default: false,
        },
        isFinalMarket: {
            type: Boolean,
            default: false,
        },
        sportId: {
            type: Number,
            default: null,
        },
    },
    setup(props) {
        const marketsStore = useVuexStore<SportOfferMarketsStore>('ui/sportOffer/markets');
        const nonFinalMarketsConfig = marketsStore.state('nonFinalMarketsConfig');
        const applyOddButtonOverrideStyle = computed(() => getPe131DescriptiveOddButtonVariant() === ExperimentsGroup.B);

        const shouldApplyMiddleLine = computed(() => {
            return !!nonFinalMarketsConfig.value?.middleLineMarkets?.[props.sportId]?.includes(props.market?.id as number);
        });

        const hasSelectionInCollapsedMarkets = computed(() => {
            if (props.selectedOdds.size === 0) {
                return false;
            }
            if (!props.market) {
                return true;
            }
            return !props.market.odds.find((x) => props.selectedOdds.has(x.uuid));
        });

        const shouldShowMoreOdds = computed(() => props.moreOdds && props.isPrimaryMarketSelected && props.isFinalMarket);

        const outcomeCount = computed(() => {
            const uniqueOutcomeIds = new Set();

            props.market?.odds.forEach((odd) => {
                uniqueOutcomeIds.add(odd.id);
            });

            return uniqueOutcomeIds.size;
        });

        function findMiddleLineOdds(odds: Odd[]) {
            let closestDiff = Number.MAX_SAFE_INTEGER;
            let index = 0;

            // In case the lines dont have odds in pairs, we cant get the middle line, so we use the same logic as normal
            if (odds.length % 2 !== 0) {
                return odds.slice(0, outcomeCount.value);
            }

            // The middle line is the line that has the smallest difference between the odds
            for (let i = 0; i < odds.length; i += 2) {
                const value1 = odds[i].value;
                const value2 = odds[i + 1].value;
                const diff = Math.abs(value1 - value2);

                if (diff < closestDiff) {
                    closestDiff = diff;
                    index = i;
                }
            }

            return odds.slice(index, index + outcomeCount.value);
        }

        const oddsList = computed(() => {
            if (props.isFinalMarket) {
                return props.market?.odds;
            }

            if (shouldApplyMiddleLine.value) {
                return findMiddleLineOdds(props.market?.odds || []);
            }

            return props.market?.odds.slice(0, outcomeCount.value);
        });

        const shouldExtendWidth = computed(() => !props.isFinalMarket && oddsList.value?.length === NON_FINAL_MAX_ODDS);

        const formatDisplayName = (odd: any) => {
            if (getPe131DescriptiveOddButtonVariant() === ExperimentsGroup.B) {
                return odd.description;
            }
            return odd.displayValue;
        };

        return {
            hasSelectionInCollapsedMarkets,
            shouldShowMoreOdds,
            oddsList,
            shouldExtendWidth,
            formatDisplayName,
            applyOddButtonOverrideStyle,
        };
    },
    components: {
        EventCardLabel,
        EventPin,
        Pick,
    },
});
